import { useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  IconButton,
  Badge,
  Typography,
  Drawer,
  Tooltip,
} from "@mui/material";
import {
  NotificationsOutlined as NotificationIcon,
  ChevronRight as ChevronRightIcon,
  SettingsOutlined as SettingsIcon,
  PeopleOutlined as PeopleIcon,
  LogoutOutlined as LogoutIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext.jsx";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { notificationAtom } from "../../recoil/atoms/notificationAtom.js";
import NotificationPopover from "../../components/NotificationPopover.jsx";
import { useUser } from "../../hooks/user.js";
import { logout } from "../../services/authService.js";
import UserMenu from "../../components/UserMenu.jsx";
import { colorsFoundation } from "../../theme/index.js";
import { componentHeights } from "../../theme/tokens/dimensions.js";
import MenuIcon from "@mui/icons-material/Menu";
import Sidebar from "./Sidebar.jsx";
import { useMediaQuery, useTheme } from "@mui/material";
import { activeBrand } from "../../theme/brands/index.js";

const logo_src = activeBrand.assets.logo;

function getUnreadCount(notifications) {
  if (!Array.isArray(notifications)) {
    return 0;
  }

  return notifications.filter((notification) => !notification?.seen_at).length;
}

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 0,
  height: componentHeights.header, // 56px, matches product design
  padding: `0 ${theme.spacing(2)}`,
  paddingLeft: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  width: "100%",
  boxSizing: "border-box",
  [theme.breakpoints.up("sm")]: {
    padding: `0 ${theme.spacing(4)}`,
    justifyContent: "flex-end",
  },
}));

const BreadcrumbArea = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
  flexWrap: "wrap",
});

const UserSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

// Notification bell — bordered rounded square (matches the Klick-Dummy).
// Accent tints come from the brand ramp so it stays skinnable; neutral greys
// come from the shared cool-grey chrome ramp (colorsFoundation.gray).
const BellButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: "36px",
  height: "36px",
  padding: 0,
  borderRadius: "8px",
  border: `1.5px solid ${
    open ? colorsFoundation.key.blue[200] : colorsFoundation.gray[200]
  }`,
  backgroundColor: open ? colorsFoundation.key.blue[50] : "transparent",
  color: open ? theme.palette.primary.main : colorsFoundation.gray[500],
  transition: "all .15s",
  "&:hover": {
    backgroundColor: open ? colorsFoundation.key.blue[50] : "transparent",
    borderColor: colorsFoundation.key.blue[300],
  },
  "& .MuiSvgIcon-root": {
    fontSize: "17px",
    color: "inherit",
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: "9px",
    fontWeight: 700,
    lineHeight: 1,
    minWidth: "17px",
    height: "17px",
    padding: 0,
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: "50%",
  },
}));

const UserAvatar = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  minWidth: "32px",
  borderRadius: "50%",
  backgroundColor: colorsFoundation.gray[700],
}));

const AvatarText = styled(Typography)(() => ({
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: 1,
  color: colorsFoundation.neutral.white,
}));

const UserInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const UserName = styled(Typography)(() => ({
  fontSize: "13px",
  fontWeight: 600,
  lineHeight: 1,
  color: colorsFoundation.gray[900],
}));

const UserRole = styled(Typography)(() => ({
  fontSize: "11px",
  fontWeight: 400,
  lineHeight: 1,
  color: colorsFoundation.gray[400],
  marginTop: "2px",
}));

const UserContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
}));

/**
 * Generate initials from a full name
 * @param {string} name - Full name
 * @returns {string} Initials (max 2 characters)
 */
function getInitials(name) {
  if (!name) {
    return "U";
  }

  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function Header({ onNotificationClick }) {
  const { user, loading: userLoading } = useUser();
  const { breadcrumbs } = useBreadcrumb();
  const notifications = useRecoilValue(notificationAtom);
  const setNotifications = useSetRecoilState(notificationAtom);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const notificationCount = useMemo(
    () => getUnreadCount(notifications),
    [notifications]
  );

  const handleNotificationButtonClick = (event) => {
    if (onNotificationClick) {
      onNotificationClick(event);
    }

    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationPopoverClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
    }
    handleUserMenuClose();
  };

  const handleSettingsClick = () => {
    navigate("/account");
    handleUserMenuClose();
  };

  const handleSubstitutionsClick = () => {
    navigate("/substitutions");
    handleUserMenuClose();
  };

  const userMenuItems = [
    {
      label: "Einstellungen",
      icon: SettingsIcon,
      onClick: handleSettingsClick,
    },
    {
      label: "Stellvertretungen",
      icon: PeopleIcon,
      onClick: handleSubstitutionsClick,
    },
    {
      label: "Abmelden",
      icon: LogoutIcon,
      onClick: handleLogout,
    },
  ];

  const user_name =
    String(user?.vorname + " " + user?.nachname).trim() || "Vorname Name";
  const userRole = user?.kundenname || "Verwalter";
  const userInitials = getInitials(user_name);

  // Skinning: where to show the user display — "header", "sidebar" or "both".
  // The header user block is hidden when a skin opts for sidebar-only.
  const userDisplay = activeBrand.features?.userDisplay ?? "both";
  const showHeaderUser = userDisplay === "header" || userDisplay === "both";

  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <HeaderContainer>
      {!isDesktop && (
        <>
          {/* Burger-Icon nur auf kleinen Screens */}
          <IconButton
            sx={{ display: { md: "none" } }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box
            component={Link}
            to="/home"
            sx={{
              display: { md: "none" },
              ml: 1,
              mr: "auto",
              height: 40,
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <img
              src={logo_src}
              alt={activeBrand.assets.logoAlt}
              style={{ height: 40 }}
            />
          </Box>

          {/* Drawer mit Sidebar */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{ display: { md: "none" } }}
          >
            <Sidebar logoSrc={logo_src} onToggle={() => {}} />
          </Drawer>
        </>
      )}

      {isDesktop && (
        <BreadcrumbArea>
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {index > 0 && (
                  <ChevronRightIcon
                    sx={{ fontSize: "16px", color: colorsFoundation.gray[300] }}
                  />
                )}
                <Typography
                  component={isLast ? "span" : Link}
                  to={isLast ? undefined : crumb.path}
                  sx={{
                    fontSize: "14px",
                    fontWeight: isLast ? 600 : 500,
                    lineHeight: 1.5,
                    color: isLast
                      ? colorsFoundation.gray[900]
                      : colorsFoundation.gray[400],
                    textDecoration: "none",
                    cursor: isLast ? "default" : "pointer",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      color: isLast ? colorsFoundation.gray[900] : "primary.main",
                    },
                  }}
                >
                  {crumb.name}
                </Typography>
              </Box>
            );
          })}
        </BreadcrumbArea>
      )}

      <UserSection>
        <StyledBadge
          badgeContent={notificationCount}
          invisible={notificationCount === 0}
        >
          <BellButton
            open={Boolean(notificationAnchorEl)}
            onClick={handleNotificationButtonClick}
            aria-label="notifications"
          >
            <NotificationIcon />
          </BellButton>
        </StyledBadge>

        {showHeaderUser && (
          <UserContainer
            sx={{ cursor: "pointer" }}
            onClick={handleUserMenuOpen}
          >
            <UserAvatar>
              <AvatarText>{userInitials}</AvatarText>
            </UserAvatar>
            <UserInfo>
              <UserName>{user_name}</UserName>
              <Tooltip title={userRole} arrow>
                <UserRole
                  sx={{
                    maxWidth: 120,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {userRole}
                </UserRole>
              </Tooltip>
            </UserInfo>
          </UserContainer>
        )}
      </UserSection>
      <NotificationPopover
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationPopoverClose}
        bellNotifications={notifications}
        setBellNotifications={setNotifications}
      />

      {/* User Menu */}
      <UserMenu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        menuItems={userMenuItems}
      />
    </HeaderContainer>
  );
}

Header.propTypes = {
  onNotificationClick: PropTypes.func,
};
